import { useEffect, useState } from 'react';

import { File01Icon } from '@hugeicons/core-free-icons';
import { FilesFolders } from 'constants/files-folders-enum';
import { textWithReferences } from 'helpers/form';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { IconComponent } from 'components/Icon/IconComponent';

import {
  checkDocumentRequestStatus,
  handleDocumentRequestUploadComplete,
  selectCandidateApplication,
} from 'api/candidate-application-slice';

import styles from './CandidateDocumentRequest.module.scss';

const CandidateDocumentRequest = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const field_code = searchParams.get('field_code') ?? '';

  const {
    application: { candidate, brand },
  } = useSelector(selectCandidateApplication);

  const metadataParam = searchParams.get('metadata');
  let uploadMetadata = {};
  if (metadataParam) {
    try {
      uploadMetadata = JSON.parse(metadataParam);
    } catch (e) {
      console.error('Failed to parse metadata from URL:', e);
    }
  }

  const noUpload = searchParams.get('no_upload') === 'true';
  const documentName = searchParams.get('document_name') ?? 'Document';
  const customMessage = searchParams.get('message');

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token || '');

  const [uploadId, setUploadId] = useState(null);
  const [documentRequestInactive, setDocumentRequestInactive] = useState(false);
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'team@checkmate.tech';

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: `Candidate Document Request - ${field_code || 'document'}`,
    });

    window.scrollTo(0, 0);
    document.title = 'Candidate Document Request | Checkmate';
  }, [field_code]);

  // Check if document request is already completed on page load
  useEffect(() => {
    const checkIfCompleted = async () => {
      const documentRequestId = uploadMetadata?.document_request_id;
      if (!token || !documentRequestId) return;

      try {
        const result = await dispatch(
          checkDocumentRequestStatus({ token, document_request_id: documentRequestId }),
        ).unwrap();

        if (result?.completed) {
          // Redirect to success page if already completed
          navigate(`/thanks?only_info=true`);
        } else if (result?.inactive) {
          // Document request no longer exists (e.g. deleted by cron)
          setDocumentRequestInactive(true);
        }
      } catch (error) {
        // If check fails, just continue showing the page
        console.error('Failed to check document request status:', error);
      }
    };

    checkIfCompleted();
  }, [token, uploadMetadata?.document_request_id, dispatch, navigate]);

  // Instructions from field_code: "Instructions Previously Provided (For Your Reference)"
  const instructionsTitle = t(`${field_code}.title`, { ns: 'candidate_fields', defaultValue: '' });
  const instructionsText = t(`${field_code}.text`, { ns: 'candidate_fields', defaultValue: '' });
  const instructionsHelpText = t(`${field_code}.help_text`, {
    ns: 'candidate_fields',
    defaultValue: '',
  });
  const hasInstructions = instructionsText || instructionsHelpText;

  const textParams = {
    'env.supportEmail': process.env.REACT_APP_SUPPORT_EMAIL || 'team@checkmate.tech',
  };

  const processedTitle = textWithReferences(instructionsTitle, textParams);
  const processedText = textWithReferences(instructionsText, textParams);
  const processedHelpText = textWithReferences(instructionsHelpText, textParams);

  // Handle upload change - when upload succeeds, notify backend
  const handleUploadChange = async uploadId => {
    setUploadId(uploadId);
    setDocumentRequestInactive(false);

    // If upload succeeded and we have a token, notify backend
    // Backend will find the matching candidate check using token (candidate_id) and upload metadata (check_type)
    if (uploadId && token) {
      try {
        await dispatch(
          handleDocumentRequestUploadComplete({ token, upload_id: uploadId }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to notify backend of upload completion:', error);
        // Show message when document request no longer exists (e.g. deleted by cron)
        if (error?.error?.log === 'document_request_inactive') {
          setDocumentRequestInactive(true);
        }
        // Other errors (e.g. "already received"): upload succeeded but backend rejected; candidate can still proceed
      }
    }
  };

  // Show instructions with optional upload (if token is provided)
  return (
    <CandidatePage token={token} withTopLogo brand={brand} allowAfterSubmission={true}>
      <CandidatePage.View>
        <CandidatePage.Card>
          <div className={styles.content}>
            <div className="d-flex flex-column u-gap-x-large">
              <div className="d-flex flex-column u-gap-medium">
                <IconComponent icon={File01Icon} size="xxlarge" />
                <div className="d-flex flex-column u-gap-small">
                  <h1 className="primary-text u-font-size-xx-large u-font-weight-500 u-margin-0">
                    {t('common.hello')} {candidate.first_name},
                  </h1>
                  <span className="secondary-text u-font-size-x-small u-font-weight-400">
                    {documentRequestInactive
                      ? t('candidate.documentRequest.noLongerActiveSubtitle')
                      : noUpload
                        ? t('candidate.documentRequest.pleaseReferToInstructions', { documentName })
                        : t('candidate.documentRequest.pleaseProvideYourDocument', {
                            documentName,
                          })}
                  </span>
                </div>
              </div>
              {documentRequestInactive && (
                <p className="secondary-text u-font-size-small u-font-weight-400 u-margin-0">
                  {t('candidate.documentRequest.noLongerActive', { supportEmail })}
                </p>
              )}
              {customMessage && (
                <p
                  className="secondary-text u-font-size-small u-font-weight-400 u-margin-0"
                  dangerouslySetInnerHTML={{ __html: customMessage }}
                />
              )}
              {token && !noUpload && !documentRequestInactive && (
                <>
                  <div className="d-flex flex-column u-gap-medium u-border u-padding u-border-radius">
                    <div className="d-flex flex-column u-gap-small">
                      <span className={`primary-text u-font-size-large u-font-weight-500`}>
                        {t('common.uploadDocument')}
                      </span>
                      <span className="secondary-text u-font-size-x-small u-font-weight-400">
                        {t('candidate.documentRequest.pleaseUploadDocument')}
                      </span>
                    </div>
                    <DocumentUpload
                      value={uploadId}
                      onChange={handleUploadChange}
                      modelParams={{}} // candidate_id will be extracted from token by backend
                      metadata={uploadMetadata}
                      bucketFolder={
                        uploadMetadata?.bucket_folder || FilesFolders.CandidatesApplicationDocuments
                      }
                      accept={{
                        'application/pdf': ['.pdf'],
                        'application/msword': ['.doc', '.docx'],
                        'image/*': [],
                      }}
                      documentTitle={documentName}
                      getUpload={getUpload}
                      signUpload={signUpload}
                      createUpload={createUpload}
                      deleteUpload={deleteUpload}
                      allowDisableFormActions={false}
                      brand={brand}
                    />
                  </div>
                </>
              )}
              {hasInstructions && (
                <div className="d-flex flex-column u-gap-small">
                  <span className="primary-text u-font-size-large u-font-weight-500 d-flex align-items-center u-gap-small">
                    {t('candidate.documentRequest.instructionsPreviouslyProvided')}{' '}
                    <span className="secondary-text u-font-size-x-small u-font-weight-400 u-font-style-italic">
                      {t('candidate.documentRequest.forYourReference')}
                    </span>
                  </span>
                  <DisplayBox show={true} alignLeft>
                    <div className={`d-flex flex-column u-gap-small ${styles.instructions}`}>
                      {instructionsTitle && (instructionsText || instructionsHelpText) && (
                        <span
                          className="primary-text u-font-size-large u-font-weight-500 white-space-pre-line"
                          dangerouslySetInnerHTML={{ __html: processedTitle ?? '' }}
                        />
                      )}
                      {instructionsText && (
                        <div>
                          <span
                            className="secondary-text u-font-size-small font-light white-space-pre-line"
                            dangerouslySetInnerHTML={{ __html: processedText ?? '' }}
                          />
                        </div>
                      )}
                      {instructionsHelpText && (
                        <div>
                          <span
                            className="secondary-text u-font-size-small font-light white-space-pre-line"
                            dangerouslySetInnerHTML={{ __html: processedHelpText ?? '' }}
                          />
                        </div>
                      )}
                    </div>
                  </DisplayBox>
                </div>
              )}
              <CustomButton
                type="primary"
                brand={brand}
                icon={noUpload || documentRequestInactive ? null : 'arrow-right'}
                disabled={!noUpload && !documentRequestInactive && !uploadId}
                prependIcon={false}
                onClick={() => {
                  const successUrl =
                    noUpload || documentRequestInactive ? `/thanks?only_info=true` : `/thanks`;
                  navigate(successUrl);
                }}
                aria-label={
                  noUpload || documentRequestInactive ? t('common.close') : t('common.submit')
                }>
                {noUpload || documentRequestInactive
                  ? t('common.close')
                  : t('common.submitDocument')}
              </CustomButton>
              <span className="secondary-text u-font-size-x-small u-font-weight-400 text-center">
                {t('common.contactAssistance')}{' '}
                <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'team@checkmate.tech'}`}>
                  {process.env.REACT_APP_SUPPORT_EMAIL || 'team@checkmate.tech'}
                </a>
                .
              </span>
            </div>
          </div>
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateDocumentRequest;
